/**
 * components/Button
 * ──────────────────────────────────────────────────────────────────────────
 * The only button in SK Company Cash. Six heights × four variants covers every
 * button in the design, which is why no screen declares its own button style.
 *
 * Behaviour worth knowing:
 *  - `loading` disables the button and keeps its label, so a pressed "Approve"
 *    cannot be pressed twice while the request is in flight. The width does not
 *    change, because a button that resizes mid-press moves the next one under
 *    the pointer.
 *  - Hover is web-only and is applied through Pressable's state callback, so
 *    Android and iOS get the pressed state instead of a stuck hover.
 *  - An `icon` renders 7px before the label — the gap the header's "＋ Assign
 *    cash" uses.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View, type ViewStyle } from 'react-native';
import { neutral, status } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { sans } from '../../theme/typography';
import { isWeb } from '../../lib/responsive';
import Icon, { type IconName } from '../Icon';
import { SIZE_SPEC, type ButtonSize, type ButtonVariant } from './types';

export interface ButtonProps {
  label: string;
  onPress?: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  /** Stroke width for the icon. The plus glyph wants 1.6; most want 1.4. */
  iconStrokeWidth?: number;
  disabled?: boolean;
  loading?: boolean;
  /** Fill the parent — used by the drawer's two-column action grid. */
  fullWidth?: boolean;
  style?: ViewStyle;
  /** Accessibility label when the visible text is not enough on its own. */
  accessibilityLabel?: string;
  testID?: string;
}

/** Resolved colours for one variant, in each of its two states. */
interface VariantColors {
  bg: string;
  border: string;
  text: string;
  hoverBg: string;
  hoverBorder: string;
}

const VARIANTS: Record<ButtonVariant, VariantColors> = {
  primary: {
    bg: neutral.ink,
    border: neutral.ink,
    text: neutral.surface,
    hoverBg: neutral.ink2,
    hoverBorder: neutral.ink2,
  },
  outline: {
    bg: neutral.surface,
    border: neutral.border,
    text: neutral.ink,
    hoverBg: neutral.surface2,
    hoverBorder: neutral.borderStrong,
  },
  danger: {
    bg: status.danger.bg,
    border: status.danger.border,
    text: status.danger.text,
    hoverBg: '#F7E2DF',
    hoverBorder: status.danger.text,
  },
  // The drawer's equal-weight action: a stronger border, and on hover the
  // border goes ink rather than the fill going dark.
  drawer: {
    bg: neutral.surface,
    border: neutral.borderStrong,
    text: neutral.ink,
    hoverBg: neutral.surface2,
    hoverBorder: neutral.ink,
  },
};

export function Button({
  label,
  onPress,
  variant = 'outline',
  size = 'lg',
  icon,
  iconStrokeWidth = 1.4,
  disabled = false,
  loading = false,
  fullWidth = false,
  style,
  accessibilityLabel,
  testID,
}: ButtonProps) {
  const [hovered, setHovered] = useState(false);
  const spec = SIZE_SPEC[size];
  const colors = VARIANTS[variant];
  const isPrimary = variant === 'primary';
  const inactive = disabled || loading;

  // Hover is only meaningful where there is a pointer, and a stuck hover after
  // a touch is worse than none at all.
  const active = hovered && isWeb && !inactive;

  const containerStyle = useMemo<ViewStyle>(
    () => ({
      height: spec.height,
      paddingHorizontal: isPrimary ? spec.paddingPrimary : spec.paddingOutline,
      backgroundColor: active ? colors.hoverBg : colors.bg,
      borderColor: active ? colors.hoverBorder : colors.border,
      // The drawer action is the one 6px-radius button in the design.
      borderRadius: variant === 'drawer' ? radii.box : radii.control,
      justifyContent: variant === 'drawer' ? 'flex-start' : 'center',
      alignSelf: fullWidth ? 'stretch' : 'flex-start',
      opacity: inactive ? 0.55 : 1,
    }),
    [spec, isPrimary, active, colors, variant, fullWidth, inactive],
  );

  return (
    <Pressable
      onPress={inactive ? undefined : onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      disabled={inactive}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityState={{ disabled: inactive, busy: loading }}
      testID={testID}
      style={[styles.base, containerStyle, style]}
    >
      {!!icon && (
        <Icon
          name={icon}
          size={13}
          color={colors.text}
          strokeWidth={iconStrokeWidth}
          // The icon sits at full strength inside a button; the 0.72 opacity
          // is a sidebar-only treatment.
        />
      )}
      <Text
        numberOfLines={1}
        style={[styles.label, { fontSize: spec.fontSize, color: colors.text }]}
      >
        {label}
      </Text>
      {loading && (
        // Sits after the label so the label does not shift when it appears.
        <View style={styles.spinner}>
          <ActivityIndicator size="small" color={colors.text} />
        </View>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  base: {
    flexDirection: 'row',
    alignItems: 'center',
    // 7px is the design's icon-to-label gap on the header primary.
    gap: 7,
    borderWidth: 1,
    // Buttons never wrap or shrink — a truncated verb is not a verb.
    flexShrink: 0,
  },
  label: {
    ...sans('500'),
  },
  spinner: {
    // Reserve nothing: RN's ActivityIndicator at "small" is 20px and the
    // button already has its height, so it simply sits inline.
    marginLeft: 2,
  },
});

export default Button;
export type { ButtonSize, ButtonVariant };
